import React, { useMemo } from 'react';
import JSON5 from 'json5';
import styled from 'styled-components';
import { ReportAnswers } from '../FeedbackReport';
import {
  CustomQuestion,
  CustomQuestionAnswer,
  CustomQuestionType,
} from 'admin/pages/ManageAccounts/CustomQuestions/utilts';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { useCustomQuestions } from 'admin/pages/ManageAccounts/CustomQuestions/hook';
import { RenderHtml } from 'npo/pages/ReportBuilder/Components/Review/ReportCustomQuestionType/utils';

export const getSurveyAnswer = (
  questionType: CustomQuestionType,
  answers?: CustomQuestionAnswer[],
) => {
  if (!answers) return undefined;
  switch (questionType) {
    case 'checkbox': {
      if (!answers[0]) return undefined;
      const a = JSON5.parse(answers[0].value);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return a.map((x: any) => x.label).join(', ');
    }
    case 'comment':
    case 'text':
    case 'integer':
    case 'multi-text':
    case 'multi-integer':
    case 'dropdown':
    case 'matrix-dropdown':
    case 'radio-group':
    case 'guidance':
      return answers.map(x => x.value).join(', ');
  }
};

const getQuestionsAndAnswers = (customQuestions: CustomQuestion[]) => {
  const result: {
    questionId: number | string;
    answer: string;
    question: string;
    questionType: CustomQuestionType;
  }[] = [];
  for (const c of customQuestions) {
    for (const q of c.questions) {
      const answer = getSurveyAnswer(q.questionType, q.answer);
      if (!answer) continue;
      result.push({
        questionId: q.dbId,
        question: q.questionText,
        questionType: q.questionType,
        answer,
      });
    }
  }
  return result;
};

interface ReportCustomQuestionsProps {
  reportId: number | string | undefined;
  answers: ReportAnswers;
}

export const StyledReportCustomQuestions = styled.div`
  border-radius: ${SPACERS.extraTiny};
  background-color: ${COLORS.whiteBase};
  border: 1px solid ${COLORS.grayLight};
  padding: ${SPACERS.large} ${SPACERS.extraLarge} ${SPACERS.medium}
    ${SPACERS.extraLarge};
  margin: ${SPACERS.large} ${SPACERS.medium};
  .report-custom-question {
    margin-bottom: ${SPACERS.small};
    .report-custom-question-text {
      margin-bottom: ${SPACERS.extraTiny};
    }
  }
`;

const ReportCustomQuestions: React.FC<ReportCustomQuestionsProps> = ({
  reportId,
}) => {
  const { customQuestionsWithoutGuidance, isLoading } = useCustomQuestions({
    reportId,
  });

  const answers = useMemo(() => {
    if (isLoading) return [];
    return getQuestionsAndAnswers(customQuestionsWithoutGuidance);
  }, [isLoading, customQuestionsWithoutGuidance]);

  if (answers.length === 0) return null;
  return (
    <StyledReportCustomQuestions>
      <TrueImpactText variant="heading-label">Report Survey</TrueImpactText>
      {answers.map(x => (
        <div key={x.questionId} className="report-custom-question">
          <TrueImpactText
            variant="body-small-bold"
            className="report-custom-question-text"
          >
            <RenderHtml text={x.question} />
          </TrueImpactText>
          <TrueImpactText variant="body-small">{x.answer}</TrueImpactText>
        </div>
      ))}
    </StyledReportCustomQuestions>
  );
};

export default ReportCustomQuestions;
